import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { Category, LabListItem } from '../../types/content';
import Input from '../../components/UI/Input';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CatalogFilterLayout, { CatalogFilterSelect } from '../../components/content/CatalogFilterLayout';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';
import CourseRating from '../../components/content/CourseRating';
import { focusPageTop } from '../../utils/focusPageTop';

const LabsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [labs, setLabs] = useState<LabListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || '');
  const [difficulty, setDifficulty] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCategoryId(searchParams.get('category') || '');
  }, [searchParams]);

  useEffect(() => {
    contentApi.listCategories('Course').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    contentApi.listLabs({
      search: search || undefined,
      categoryId: categoryId || undefined,
      difficulty: difficulty || undefined,
      pageSize: 50,
    }).then((r) => setLabs(r.data.labs || [])).finally(() => setLoading(false));
  }, [search, categoryId, difficulty]);

  useEffect(() => {
    focusPageTop();
  }, [search, categoryId, difficulty]);

  return (
    <div>
      <section className={heroShell('labs')}>
        <HeroBackdrop tone="labs" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Self paced - Practice</p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                Hands-on labs you can start right away
              </h1>
              <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">
                Explore hands-on labs, follow practical steps at your own pace, and build skills as you go. Browse public labs without an account.
              </p>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.labsHero}
                  alt="Engineers practicing in a hands-on lab"
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover object-center"
                />
                <div className={heroImageFade('labs')} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <CatalogFilterLayout
        applied={[
          ...(search.trim() ? [{ key: 'search', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') }] : []),
          ...(categoryId ? [{ key: 'category', label: `Category: ${categories.find((c) => String(c.id) === String(categoryId))?.name || categoryId}`, onRemove: () => setCategoryId('') }] : []),
          ...(difficulty ? [{ key: 'difficulty', label: `Difficulty: ${difficulty}`, onRemove: () => setDifficulty('') }] : []),
        ]}
        onClearFilters={() => {
          setSearch('');
          setCategoryId('');
          setDifficulty('');
        }}
        results={(
          <>
            {loading && <PageLoading label="Loading labs…" />}
            {!loading && labs.length === 0 && <p className="text-gray-500">No public labs match these filters.</p>}
            {labs.length > 0 && (
              <div className="catalog-panel">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Title</th>
                      <th className="px-4 py-3 font-semibold">Subtitle</th>
                      <th className="px-4 py-3 font-semibold">Category</th>
                      <th className="px-4 py-3 font-semibold">Course</th>
                      <th className="px-4 py-3 font-semibold">Technology</th>
                      <th className="px-4 py-3 font-semibold">Difficulty</th>
                      <th className="px-4 py-3 font-semibold">Duration</th>
                      <th className="px-4 py-3 font-semibold">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {labs.map((lab) => (
                      <tr key={lab.id} className="hover:bg-primary-50/40">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <Link to={`/labs/${lab.slug}`} className="text-primary-700 hover:underline">
                            {lab.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs">{lab.subtitle || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{lab.categoryName || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {lab.courseSlug && lab.courseTitle ? (
                            <Link to={`/courses/${lab.courseSlug}`} className="text-primary-700 hover:underline">
                              {lab.courseTitle}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{lab.technologyName || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{lab.difficulty}</td>
                        <td className="px-4 py-3 text-gray-700">{lab.duration || '—'}</td>
                        <td className="px-4 py-3"><CourseRating average={lab.averageRating} count={lab.ratingCount} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      >
        <Input id="lab-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <CatalogFilterSelect label="Category" value={categoryId} onChange={setCategoryId}>
          <option value="">All</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </CatalogFilterSelect>
        <CatalogFilterSelect label="Difficulty" value={difficulty} onChange={setDifficulty}>
          <option value="">All</option>
          <option>Beginner</option>
          <option>Intermediate</option>
          <option>Advanced</option>
        </CatalogFilterSelect>
      </CatalogFilterLayout>
    </div>
    </div>
  );
};

export default LabsPage;
